import { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Input, Space, Card, Row, Col } from "antd";
import { SearchOutlined, ClearOutlined, PlusOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import locationService from "../services/locationService";

const { Search } = Input;

const Countries = () => {
  const [countries, setCountries] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [deletingCountry, setDeletingCountry] = useState(null);
  const [form] = Form.useForm();
  
  // Arama ve filtreleme state'leri
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState({
    name: '',
    code: '',
    phoneCode: ''
  });

  // Ülkeleri çek
  const fetchCountries = async () => {
    setLoading(true);
    try {
      const result = await locationService.getAllCountries();
      setCountries(result);
      setFilteredCountries(result);
    } catch {
      toast.error("Ülkeler alınamadı.");
    } finally {
      setLoading(false);
    }
  };

  // Genel arama fonksiyonu
  const handleSearch = (value) => {
    setSearchText(value);
    applyFilters(value, filters);
  };

  // Detaylı filtreleme fonksiyonu
  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    applyFilters(searchText, newFilters);
  };

  // Filtreleri uygula
  const applyFilters = (search, detailFilters) => {
    let filtered = [...countries];

    // Genel arama (tüm alanlarda ara)
    if (search) {
      filtered = filtered.filter(country => 
        country.name?.toLowerCase().includes(search.toLowerCase()) ||
        country.code?.toLowerCase().includes(search.toLowerCase()) ||
        country.phoneCode?.toLowerCase().includes(search.toLowerCase())
      );
    }

    // Detaylı filtreler
    if (detailFilters.name) {
      filtered = filtered.filter(country => 
        country.name?.toLowerCase().includes(detailFilters.name.toLowerCase())
      );
    }
    if (detailFilters.code) {
      filtered = filtered.filter(country => 
        country.code?.toLowerCase().includes(detailFilters.code.toLowerCase())
      );
    }
    if (detailFilters.phoneCode) {
      filtered = filtered.filter(country => 
        country.phoneCode?.toLowerCase().includes(detailFilters.phoneCode.toLowerCase())
      );
    }

    setFilteredCountries(filtered);
  };

  // Filtreleri temizle
  const clearFilters = () => {
    setSearchText('');
    setFilters({ name: '', code: '', phoneCode: '' });
    setFilteredCountries(countries);
  };

  // Ekle / Güncelle
  const handleAddOrUpdate = async (values) => {
    try {
      let response;
      if (editingCountry) {
        response = await locationService.updateCountry({ ...editingCountry, ...values });
      } else {
        response = await locationService.addCountry(values);
      }

      if (response?.Status) {
        toast.success(response?.Message || response?.MessageCode || "İşlem başarılı.");
        fetchCountries();
        setIsModalOpen(false);
        form.resetFields();
        setEditingCountry(null);
      } else {
        toast.error(response?.Message || "İşlem başarısız.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.Message || "İşlem sırasında hata oluştu.");
    }
  };

  // Sil
  const handleDelete = async (id) => {
    try {
      const response = await locationService.deleteCountry(id);
      if (response?.Status) {
        toast.success(response?.Message || "Silme işlemi başarılı.");
        fetchCountries();
      } else {
        toast.error(response?.Message || "Silme işlemi başarısız.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.Message || "Silme sırasında hata oluştu.");
    }
  };

  useEffect(() => {
    fetchCountries();
  }, []);

  // Filtrelerin aktif olup olmadığını kontrol et
  const hasActiveFilters = searchText || filters.name || filters.code || filters.phoneCode;
  const resultCount = filteredCountries.length;

  const columns = [
    { 
      title: "Ad", 
      dataIndex: "name",
      sorter: (a, b) => a.name?.localeCompare(b.name),
      render: (text, record) => (
        <span style={{ 
          backgroundColor: filters.name && text?.toLowerCase().includes(filters.name.toLowerCase()) ? '#fff2e8' : 'transparent',
          padding: '2px 4px',
          borderRadius: '3px'
        }}>
          {text}
        </span>
      )
    },
    { 
      title: "Kod", 
      dataIndex: "code",
      sorter: (a, b) => a.code?.localeCompare(b.code),
      render: (text, record) => (
        <span style={{ 
          backgroundColor: filters.code && text?.toLowerCase().includes(filters.code.toLowerCase()) ? '#fff2e8' : 'transparent',
          padding: '2px 4px',
          borderRadius: '3px'
        }}>
          {text}
        </span>
      )
    },
    { 
      title: "Telefon Kodu", 
      dataIndex: "phoneCode",
      sorter: (a, b) => a.phoneCode?.localeCompare(b.phoneCode),
      render: (text, record) => (
        <span style={{ 
          backgroundColor: filters.phoneCode && text?.toLowerCase().includes(filters.phoneCode.toLowerCase()) ? '#fff2e8' : 'transparent',
          padding: '2px 4px',
          borderRadius: '3px'
        }}>
          {text}
        </span>
      )
    },
    {
      title: "İşlem",
      width: 180,
      render: (text, record) => (
        <Space>
          <Button
            size="small"
            onClick={() => {
              setEditingCountry(record);
              form.setFieldsValue(record);
              setIsModalOpen(true);
            }}
          >
            Güncelle
          </Button>
          <Button
            size="small"
            danger
            onClick={() => {
              setDeletingCountry(record);
              setIsDeleteModalOpen(true);
            }}
          >
            Sil
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Ülkeler</h2>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={() => setIsModalOpen(true)}
        >
          Yeni Ülke Ekle
        </Button>
      </div>

      {/* Arama ve Filtre Alanı */}
      <Card style={{ marginBottom: '16px' }}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {/* Genel Arama */}
          <div>
            <Search
              placeholder="Tüm alanlarda ara..."
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              onSearch={handleSearch}
              style={{ width: '100%', maxWidth: '400px' }}
              enterButton={<SearchOutlined />}
              allowClear
            />
          </div>

          {/* Detaylı Filtreler */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <Input
                placeholder="Ülke adına göre filtrele"
                prefix={<SearchOutlined />}
                value={filters.name}
                onChange={(e) => handleFilterChange('name', e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={8}>
              <Input
                placeholder="Ülke koduna göre filtrele"
                prefix={<SearchOutlined />}
                value={filters.code}
                onChange={(e) => handleFilterChange('code', e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={8}>
              <Input
                placeholder="Telefon koduna göre filtrele"
                prefix={<SearchOutlined />}
                value={filters.phoneCode}
                onChange={(e) => handleFilterChange('phoneCode', e.target.value)}
                allowClear
              />
            </Col>
          </Row>

          {/* Filtre Durumu ve Temizle Butonu */}
          {hasActiveFilters && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: '#666' }}>
                <strong>{resultCount}</strong> ülke bulundu
                {countries.length !== resultCount && ` (${countries.length} toplam)`}
              </span>
              <Button 
                icon={<ClearOutlined />} 
                onClick={clearFilters}
                size="small"
              >
                Filtreleri Temizle
              </Button>
            </div>
          )}
        </Space>
      </Card>

      {/* Tablo */}
      <Table
        rowKey="id"
        loading={loading}
        dataSource={filteredCountries}
        columns={columns}
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => 
            `${range[0]}-${range[1]} / ${total} kayıt`,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
        locale={{
          emptyText: hasActiveFilters 
            ? 'Filtrelere uygun ülke bulunamadı' 
            : 'Henüz ülke bulunmuyor'
        }}
      />

      {/* Ekle / Güncelle Modalı */}
      <Modal
        title={editingCountry ? "Ülke Güncelle" : "Ülke Ekle"}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingCountry(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText={editingCountry ? "Güncelle" : "Ekle"}
        cancelText="İptal"
      >
        <Form form={form} onFinish={handleAddOrUpdate} layout="vertical">
          <Form.Item 
            name="name" 
            label="Ülke Adı" 
            rules={[
              { required: true, message: 'Ülke adı gerekli' },
              { min: 2, message: 'En az 2 karakter olmalı' }
            ]}
          >
            <Input placeholder="Örn: Türkiye" />
          </Form.Item>
          <Form.Item 
            name="code" 
            label="Ülke Kodu" 
            rules={[
              { required: true, message: 'Ülke kodu gerekli' },
              { len: 2, message: 'Tam olarak 2 karakter olmalı' }
            ]}
          >
            <Input placeholder="Örn: TR" maxLength={2} style={{ textTransform: 'uppercase' }} />
          </Form.Item>
          <Form.Item 
            name="phoneCode" 
            label="Telefon Kodu" 
            rules={[
              { required: true, message: 'Telefon kodu gerekli' },
              { pattern: /^\+?\d+$/, message: 'Sadece rakam ve + işareti kullanın' }
            ]}
          >
            <Input placeholder="Örn: +90" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Silme Modalı */}
      <Modal
        title="Silme işlemini onaylıyor musunuz?"
        open={isDeleteModalOpen}
        onCancel={() => setIsDeleteModalOpen(false)}
        onOk={() => {
          if (deletingCountry) {
            handleDelete(deletingCountry.id);
            setIsDeleteModalOpen(false);
          }
        }}
        okText="Evet, Sil"
        okType="danger"
        cancelText="Vazgeç"
      >
        <p>
          <strong>"{deletingCountry?.name}"</strong> adlı ülke kalıcı olarak silinecek. Bu işlem geri alınamaz.
        </p>
        <p style={{ color: '#ff4d4f', fontSize: '12px' }}>
          Bu ülkeye bağlı şehir ve kullanıcı verileri etkilenebilir.
        </p>
      </Modal>
    </div>
  );
};

export default Countries;